// @flow
import React, {useState, useEffect} from "react";
import {Table, Button, Tag, Space, message, Spin, Empty, Dropdown, Menu, Badge} from "antd";
import {SendOutlined, EyeOutlined, DownloadOutlined, FileOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {courierContractService} from "../../../../services/courierContractService";
import {CreateContractModal} from "./CreateContractModal";
import {UploadContractModal} from "./UploadContractModal";
import {DocumentModal} from "../../../documents/documentModal";
import {useStore} from "@vigo/hooks";
import {ContentWrapper, TableWrapper, ContractsTable} from "./CourierContractsTab.styled";
import {ContractPreviewGenerator} from "./contractPreviewGenerator/ContractPreviewGenerator";

type Props = {
    courierId: string,
    courierData: Object
};

export const CourierContractsTab = ({courierId, courierData}: Props) => {
    const {t} = useTranslation(["courierContracts", "userTypes"]);
    const stores = useStore();
    const userRole = stores.meStore.role;
    const canApproveContracts = ["admin", "superadmin", "ik", "agencyIk"].includes(userRole);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [approvalContractId, setApprovalContractId] = useState(null);

    useEffect(() => {
        loadContracts();
    }, [courierId]);

    const loadContracts = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await courierContractService.getCourierContracts(courierId, {
                page,
                limit: pageSize
            });

            if (response.data && response.data.success) {
                setContracts(response.data.result.data.contracts || []);
                setPagination({
                    current: response.data.result.data.pagination.page,
                    pageSize: response.data.result.data.pagination.limit,
                    total: response.data.result.data.pagination.total
                });
            } else {
                // Sadece console'da log'la, kullanıcıya hata mesajı gösterme
                console.error("Contract loading failed:", response);
                setContracts([]);
            }
        } catch (error) {
            // Sadece console'da log'la, kullanıcıya hata mesajı gösterme
            console.error("Contract loading error:", error);
            setContracts([]);
        } finally {
            setLoading(false);
        }
    };

    const getContractFiles = contract => contract.files || [];

    const handleViewFile = url => {
        window.open(url, "_blank");
    };

    const handleDownloadFile = (url, displayName, index) => {
        const link = document.createElement("a");
        link.href = url;
        const ext =
            url
                .split(".")
                .pop()
                ?.split("?")[0] || "pdf";
        link.download = `${displayName}${index > 0 ? `_${index + 1}` : ""}.${ext}`;
        link.click();
    };

    const getStatusColor = status => {
        const colors = {
            created: "blue",
            sent: "orange",
            signed: "green",
            rejected: "red",
            expired: "gray"
        };
        return colors[status] || "default";
    };

    const getStatusText = (status, record) => {
        if (record?.isRejected === true) {
            return "Reddedildi";
        }
        // Eğer onaylandıysa (isApproved === true ve eSignStatus !== "signed")
        if (record?.isApproved === true && record?.eSignStatus !== "signed") {
            const approvedByRole = record?.approvedBy?.role;
            if (approvedByRole) {
                // Rol çevirisini al, eğer yoksa rol adını kullan
                const roleTranslation =
                    t(approvedByRole, {ns: "userTypes"}) || approvedByRole.toUpperCase();
                return `Onaylandı(${roleTranslation})`;
            }
            // Eğer rol bilgisi yoksa varsayılan olarak "Onaylandı" göster
            return "Onaylandı";
        }
        if (status === null) {
            return "Bilinmiyor";
        }
        return t(`statuses.${status}`) || status;
    };

    const getContractTypeText = contractName => {
        return t(`contractTypes.${contractName}`) || contractName;
    };

    // Her sözleşme tipinin en güncel halini bul
    const getLatestContractByType = contractName => {
        const contractsOfType = contracts.filter(c => c.contractName === contractName);
        if (contractsOfType.length === 0) return null;

        // createdAt'e göre en yeni olanı bul
        return contractsOfType.reduce((latest, current) => {
            const latestDate = new Date(latest.createdAt);
            const currentDate = new Date(current.createdAt);
            return currentDate > latestDate ? current : latest;
        });
    };

    // Belirli bir contract'ın o tip için en güncel olup olmadığını kontrol et
    const isLatestContractOfType = contract => {
        const latestContract = getLatestContractByType(contract.contractName);
        return latestContract && latestContract._id === contract._id;
    };

    const isCourierEquipmentContract = contract => {
        return contract.contractName === "courierEquipmentAgreement";
    };

    const columns = [
        {
            title: t("table.contractName"),
            dataIndex: "displayName",
            key: "displayName",
            width: 200,
            onCell: record => ({
                "data-label": t("table.contractName")
            })
        },
        {
            title: t("table.type"),
            dataIndex: "contractName",
            key: "contractName",
            width: 150,
            render: contractName => getContractTypeText(contractName),
            onCell: record => ({
                "data-label": t("table.type")
            })
        },
        {
            title: t("table.status"),
            dataIndex: "eSignStatus",
            key: "eSignStatus",
            width: 120,
            render: (status, record) => {
                // Eğer IK tarafından onaylandıysa özel durum göster
                const displayStatus =
                    record?.isApproved === true && record?.eSignStatus !== "signed"
                        ? "approvedByIK"
                        : status;
                return (
                    <Tag
                        color={
                            record?.isRejected === true
                                ? "red"
                                : getStatusColor(
                                      displayStatus === "approvedByIK" ? "signed" : status
                                  )
                        }
                    >
                        {getStatusText(status, record)}
                    </Tag>
                );
            },
            onCell: record => ({
                "data-label": t("table.status")
            })
        },
        {
            title: t("table.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            render: date => moment(date).format("DD.MM.YYYY HH:mm"),
            onCell: record => ({
                "data-label": t("table.createdAt")
            })
        },
        {
            title: t("table.signedAt"),
            dataIndex: "signedAt",
            key: "signedAt",
            width: 150,
            render: date => (date ? moment(date).format("DD.MM.YYYY HH:mm") : "-"),
            onCell: record => ({
                "data-label": t("table.signedAt")
            })
        },
        {
            title: t("table.files"),
            key: "files",
            width: 80,
            render: (_, record) => {
                const files = getContractFiles(record);
                if (!files.length) return "-";
                return <Badge count={files.length} style={{backgroundColor: "#1890ff"}} />;
            },
            onCell: record => ({
                "data-label": t("table.files")
            })
        },
        {
            title: t("table.actions"),
            key: "actions",
            width: 250,
            render: (_, record) => {
                const files = getContractFiles(record);
                const hasMultipleFiles = files.length > 1;

                const viewMenu = (
                    <Menu>
                        {files.map((file, idx) => (
                            <Menu.Item
                                key={`view-${idx}`}
                                icon={<EyeOutlined />}
                                onClick={() => handleViewFile(file.path)}
                            >
                                {t("actions.fileIndex", {index: idx + 1})}
                            </Menu.Item>
                        ))}
                    </Menu>
                );

                const downloadMenu = (
                    <Menu>
                        {files.map((file, idx) => (
                            <Menu.Item
                                key={`dl-${idx}`}
                                icon={<DownloadOutlined />}
                                onClick={() =>
                                    handleDownloadFile(file.path, record.displayName, idx)
                                }
                            >
                                {t("actions.fileIndex", {index: idx + 1})}
                            </Menu.Item>
                        ))}
                    </Menu>
                );

                return (
                    <Space>
                        {hasMultipleFiles ? (
                            <>
                                <Dropdown overlay={viewMenu} trigger={["click"]}>
                                    <Button type="link" icon={<EyeOutlined />} size="small">
                                        {t("actions.view")}
                                    </Button>
                                </Dropdown>
                                <Dropdown overlay={downloadMenu} trigger={["click"]}>
                                    <Button type="link" icon={<DownloadOutlined />} size="small">
                                        {t("actions.download")}
                                    </Button>
                                </Dropdown>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={() => files[0] && handleViewFile(files[0].path)}
                                    size="small"
                                    disabled={!files.length}
                                >
                                    {t("actions.view")}
                                </Button>
                                <Button
                                    type="link"
                                    icon={<DownloadOutlined />}
                                    onClick={() =>
                                        files[0] &&
                                        handleDownloadFile(files[0].path, record.displayName, 0)
                                    }
                                    size="small"
                                    disabled={!files.length}
                                >
                                    {t("actions.download")}
                                </Button>
                            </>
                        )}
                        {canApproveContracts &&
                            record.eSignStatus !== "signed" &&
                            !record.isApproved &&
                            isLatestContractOfType(record) &&
                            !isCourierEquipmentContract(record) && (
                                <Button
                                    type="link"
                                    onClick={() => handleApproveContract(record)}
                                    size="small"
                                    style={{color: "#52c41a"}}
                                >
                                    Onayla
                                </Button>
                            )}
                    </Space>
                );
            },
            onCell: record => ({
                "data-label": t("table.actions")
            })
        }
    ];

    const handleTableChange = pagination => {
        loadContracts(pagination.current, pagination.pageSize);
    };

    const handleModalSuccess = () => {
        setModalVisible(false);
        setUploadModalVisible(false);
        loadContracts(pagination.current, pagination.pageSize);
    };

    const handleBulkSendToESignature = async () => {
        try {
            if (!selectedRowKeys.length) return;

            // Seçilen sözleşmelerin gönderilebilir olup olmadığını kontrol et
            const selectedContracts = contracts.filter(c => selectedRowKeys.includes(c._id));
            const invalidContracts = selectedContracts.filter(contract => {
                return !(
                    contract.eSignStatus === "created" &&
                    contract.isRequiredEsign &&
                    isLatestContractOfType(contract) &&
                    !contract.isApproved &&
                    !isCourierEquipmentContract(contract)
                );
            });

            if (invalidContracts.length > 0) {
                message.error(
                    "Seçilen sözleşmelerden bazıları e-imzaya gönderilemez. Lütfen sadece onaylanmamış ve en güncel sözleşmeleri seçin."
                );
                return;
            }

            setLoading(true);
            const response = await courierContractService.sendContractsToESignatureBulk(
                courierId,
                selectedRowKeys
            );
            if (response.data && response.data.success) {
                message.success(t("messages.contractSentToESignature"));
                setSelectedRowKeys([]);
                loadContracts(pagination.current, pagination.pageSize);
            } else {
                message.error(response.data?.message || t("messages.contractSendFailed"));
            }
        } catch (error) {
            console.error("Toplu e-imza gönderimi hatası:", error);
            message.error(t("messages.contractSendFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleApproveContract = contract => {
        const firstFilePath = contract?.files?.[0]?.path;
        if (firstFilePath) {
            setDocumentUrl(firstFilePath);
            setApprovalContractId(contract._id);
            setIsDocumentModalVisible(true);
        } else {
            console.error("Contract file path is not found!");
            message.error(t("messages.contractSendFailed"));
        }
    };

    const handleCancelDocumentModal = () => {
        setIsDocumentModalVisible(false);
        setDocumentUrl(null);
        setApprovalContractId(null);
    };

    const handleApprove = async () => {
        try {
            if (!approvalContractId) return;

            const response = await courierContractService.approveCourierContract(
                approvalContractId
            );

            if (response.data && response.data.success) {
                message.success("Sözleşme başarıyla onaylandı");
                setIsDocumentModalVisible(false);
                setDocumentUrl(null);
                setApprovalContractId(null);
                loadContracts(pagination.current, pagination.pageSize);
            } else {
                message.error(response.data?.message || "Onay işlemi başarısız oldu");
            }
        } catch (error) {
            console.error("Onay işlemi hatası:", error);
            message.error("Onay işlemi başarısız oldu");
        }
    };

    return (
        <ContentWrapper>
            <div
                style={{
                    marginBottom: "16px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <h3 style={{margin: 0}}>{t("contracts")}</h3>
                <div style={{display: "flex", gap: "8px"}}>
                    <ContractPreviewGenerator
                        courierId={courierId}
                        showOnlyCourierContractTypes={true}
                    />
                    <Button type="default" onClick={() => setUploadModalVisible(true)}>
                        {t("actions.uploadContract")}
                    </Button>
                    {!stores.meStore.checkRole("isAgency") && (
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={handleBulkSendToESignature}
                            disabled={(() => {
                                if (!selectedRowKeys.length) return true;

                                // Seçilen sözleşmelerin hepsi geçerli mi kontrol et
                                const selectedContracts = contracts.filter(c =>
                                    selectedRowKeys.includes(c._id)
                                );
                                const allValid = selectedContracts.every(
                                    contract =>
                                        contract.eSignStatus === "created" &&
                                        contract.isRequiredEsign &&
                                        isLatestContractOfType(contract) &&
                                        !contract.isApproved &&
                                        !isCourierEquipmentContract(contract)
                                );

                                return !allValid;
                            })()}
                        >
                            {t("actions.sendToESignature")}
                        </Button>
                    )}
                </div>
            </div>

            <Spin spinning={loading}>
                <TableWrapper>
                    <ContractsTable
                        columns={columns}
                        dataSource={contracts}
                        rowKey="_id"
                        rowSelection={{
                            selectedRowKeys,
                            onChange: keys => setSelectedRowKeys(keys),
                            getCheckboxProps: record => {
                                // Sadece şu kriterlere uyan sözleşmeler seçilebilir:
                                // 1. Durum "created" olmalı
                                // 2. E-imza gerekli olmalı
                                // 3. O tipin en son örneği olmalı
                                // 4. Onaylanmamış olmalı
                                const canSelect =
                                    record.eSignStatus === "created" &&
                                    record.isRequiredEsign &&
                                    isLatestContractOfType(record) &&
                                    !record.isApproved &&
                                    !isCourierEquipmentContract(record);

                                return {
                                    disabled: !canSelect
                                };
                            }
                        }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} ${t("contracts").toLowerCase()}`
                        }}
                        onChange={handleTableChange}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={t("messages.noContracts")}
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )
                        }}
                    />
                </TableWrapper>
            </Spin>

            <CreateContractModal
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onSuccess={handleModalSuccess}
                courierId={courierId}
                courierData={courierData}
            />

            <UploadContractModal
                visible={uploadModalVisible}
                onCancel={() => setUploadModalVisible(false)}
                onSuccess={handleModalSuccess}
                courierId={courierId}
                courierData={courierData}
            />

            {documentUrl && (
                <DocumentModal
                    visibility={isDocumentModalVisible}
                    onCancel={handleCancelDocumentModal}
                    documentUrl={documentUrl}
                    title="Sözleşme Görüntüleyici"
                    isNeedForApproval={true}
                    isApproved={contracts.find(c => c._id === approvalContractId)?.isApproved}
                    onApprove={handleApprove}
                    isApproveButtonDisabled={false}
                />
            )}
        </ContentWrapper>
    );
};
